import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Form, Button, Alert } from "react-bootstrap";
import "../../Assets/scss/components/Enable2FA.scss";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import { SET_MESSAGE } from "../../Actions/Types";
import { get2FASetupApi, verify2FACodeApi } from "../../Api/TwoFactorAuthAPI";
import { useSelector, useDispatch } from "react-redux";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { useNavigate } from "react-router-dom";
import DashboardPagesTemplate from "../../Template/DashboardPagesTemplate";

const Enable2FA: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accessToken } = useSelector((store: StoreProptypes) => store);

  // Local states
  const [code, setCode] = useState<string>("");
  const [qrUrl, setQrUrl] = useState<string>("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const recoveryRef = useRef<HTMLDivElement>(null);
  const isMobile = window.matchMedia("(max-width: 576px)").matches;
  const isTablet = window.matchMedia("(max-width: 991px)").matches;
  // Load QR Code on mount
  useEffect(() => {
    if (accessToken) {
      get2FASetupApi(accessToken)
        .then((res) => {
          if (res?.data) {
            setQrUrl(res?.data?.google2faUrl);
          }
        })
        .catch((err) => {
          dispatch({
            type: SET_MESSAGE,
            payload: {
              toast_message: `${err}`,
              toast_type: "danger",
            },
          });
          console.error("Failed to load 2FA QR:", err);
        });
    }
  }, [accessToken, dispatch]);

  // Verify 2FA Code
  const handleVerify = async () => {
    if (!code) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("Please enter the verification code."),
          toast_type: "danger",
        },
      });
      return;
    }

    try {
      const response = await verify2FACodeApi({ token: accessToken, code });

      if (response?.status) {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: response?.message,
            toast_type: "success",
          },
        });
        setCode("");
        setRecoveryCodes(response?.recovery_codes || []);
      } else {
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: t("Verification failed."),
            toast_type: "danger",
          },
        });
      }
    } catch (error) {
      dispatch({
        type: SET_MESSAGE,
        payload: {
          toast_message: t("OTP doesn't match, Please verify again."),
          toast_type: "danger",
        },
      });
    }
  };

  // Copy recovery codes to clipboard
  const handleCopy = () => {
    const codes = recoveryCodes.join("\n");
    navigator.clipboard.writeText(codes);
    dispatch({
      type: SET_MESSAGE,
      payload: {
        toast_message: t("Recovery codes copied."),
        toast_type: "success",
      },
    });
  };

  // Download recovery codes as a .txt file
  const handleDownload = () => {
    const element = document.createElement("a");
    const file = new Blob([recoveryCodes.join("\n")], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "recovery-codes.txt";
    document.body.appendChild(element);
    element.click();
  };

  // Print recovery codes
  const handlePrint = () => {
    if (recoveryRef.current) {
      const printWindow = window.open("", "", "width=600,height=400");
      if (printWindow) {
        printWindow.document.write(
          "<pre>" + recoveryCodes.join("\n") + "</pre>"
        );
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  return (
    <DashboardPagesTemplate>
      <div className="two-fa-wrapper">
        <div className="two-fa-header">
          <h2 className="mb-0 h5">{t("Two-factor authentication")}</h2>
        </div>

        <div className="p-4">
          {!recoveryCodes.length ? (
            // === Step 1: Show QR and input for code ===
            <>
              <p className="description">
                {t("Register a one-time password authenticator")}
              </p>
              <p>
                {t(
                  "You should enable two-factor authentication (2FA) using Google Authenticator on your mobile device or computer."
                )}
              </p>
              <div>
                {/* <Row className="qr-section mt-4" style={{ backgroundColor: "white" }}>
                  <Col md={2} xs={12}>
                    <div className="qr-code-box">
                      <QRCodeCanvas className="qr-img" value={qrUrl} size={isMobile ? 200 : isTablet ? 280 : 350} />
                    </div>
                  </Col>
                </Row> */}
                <div className="qr-section mt-4 qr-wrapper">
                  <div className="qr-code-box">
                    <QRCodeCanvas className="qr-img" value={qrUrl} size={isMobile ? 200 : isTablet ? 280 : 350} />
                  </div>
                </div>

              <Form className="">
                <Form.Group controlId="formCode" className="mt-3">
                  <Form.Label>{t("Enter verification code")}</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={t("Enter 6-digit code")}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </Form.Group>

                <Button className="mt-4" onClick={handleVerify}>
                  {t("Register with two-factor app")}
                </Button>
              </Form>
              </div>
            </>
          ) : (
            // === Step 2: Show Recovery Codes + Finish ===
            <>
              <h5 className="mt-3 mb-3">
                {t("two-factor authentication recovery codes")}
              </h5>

              <Alert variant="info">
                {t(
                  "Please copy, download, or print your recovery codes before proceeding."
                )}
              </Alert>

              <div className="recovery-actions mb-3 d-flex gap-2 flex-wrap">
                <Button
                  variant="outline-primary"
                  className="recovery-btn"
                  onClick={handleCopy}
                >
                  <i className="material-symbols-outlined">content_copy</i>{" "}
                  {t("Copy")}
                </Button>
                <Button
                  variant="outline-success"
                  className="recovery-btn"
                  onClick={handleDownload}
                >
                  <i className="material-symbols-outlined">download</i>{" "}
                  {t("Download")}
                </Button>
                <Button
                  variant="outline-dark"
                  className="recovery-btn"
                  onClick={handlePrint}
                >
                  <i className="material-symbols-outlined">print</i>{" "}
                  {t("Print")}
                </Button>
              </div>

              <div
                className="recovery-code-box styled-codes-box"
                ref={recoveryRef}
              >
                <div className="recovery-grid">
                  {recoveryCodes.map((code, idx) => (
                    <div className="code-item" key={idx}>
                      <i className="material-symbols-outlined shield-icon">
                        shield_lock
                      </i>
                      <span>{code}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Finish Button (aligned right) */}
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="primary"
                  onClick={() => navigate("/front/security")}
                >
                  {t("finish")}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardPagesTemplate>
  );
};

export default Enable2FA;
